import { useRef, useState } from 'react';
import type { ListingModel } from '../api/types';
import { floatValue } from '../lib/format';
import { useCurrency } from '../hooks/useCurrency';
import { useCart } from '../hooks/useCart';
import { useI18n } from '../i18n';
import { itemDoAnuncio, trackAddToCart, trackRemoveFromCart } from '../lib/tracking';
import { linhaDoAnuncio } from '../lib/sacola';

/** Altura aproximada da prévia, para decidir se cabe acima da linha. */
const ALTURA_PREVIA = 240;

interface Props {
  listing: ListingModel;
  rank: number;
  fallbackIcon: string | null;
  onOpenImage: (url: string, caption: string, commodityId: string | null, inspectUrl: string | null) => void;
}

export function ListingRow({ listing, rank, fallbackIcon, onOpenImage }: Props) {
  const { money, rate } = useCurrency();
  const carrinho = useCart();
  const naSacola = carrinho.contem(listing.commodityNo);
  const { t, loc } = useI18n();
  const icon = listing.iconUrl ?? fallbackIcon;
  const image = listing.imageUrl ?? icon;
  const realImage = Boolean(listing.imageUrl);

  // A prévia grande só é montada depois do primeiro hover. São dezenas de
  // anúncios por página, e baixar a foto de todos na abertura seria pagar
  // banda por imagem que ninguém vai olhar.
  const [espiou, setEspiou] = useState(false);
  // Para cima quando há espaço, para baixo quando não há. Sem isto, a prévia
  // das linhas no topo da janela abria fora da tela e simplesmente não
  // aparecia — o pior tipo de defeito, porque parece que a função não existe.
  const [paraBaixo, setParaBaixo] = useState(false);
  const miniatura = useRef<HTMLDivElement>(null);

  function aoEntrar() {
    const caixa = miniatura.current?.getBoundingClientRect();
    if (caixa) setParaBaixo(caixa.top < ALTURA_PREVIA);
    setEspiou(true);
  }

  const legenda = `${t('level3.float')} ${floatValue(listing.float)}${
    realImage ? '' : ` · ${t('level3.modelImageCaption')}`
  }`;
  const abrirImagem = () => image && onOpenImage(image, legenda, listing.commodityNo, listing.inspectUrl);

  // O estilo (fase, faixa de Fade, Tier) vira selo colorido, e não mais texto
  // corrido: é o que distingue duas peças do mesmo desgaste, e o Tier do Case
  // Hardened precisa saltar aos olhos de quem está comparando.
  const temFloat = listing.float !== null && listing.float !== undefined && Number.isFinite(listing.float);

  const estilo = listing.style;
  // Doppler cuja fase não reconhecemos: melhor mostrar o texto do vendedor do
  // que engolir a informação.
  const estiloCru = !estilo ? (listing.doppler?.name ?? null) : null;
  const fadePct = listing.fade?.number ?? null;

  const details = [
    listing.paintSeed !== null ? `${t('level3.pattern')} ${listing.paintSeed}` : null,
    // O selo diz a faixa ("Fade 90–95%"); quem compra Fade quer o número exato.
    fadePct !== null ? `${t('level3.fadePct')}: ${fadePct.toFixed(1)}` : null,
    estiloCru,
    listing.nameTag ? `“${listing.nameTag}”` : listing.hasNameTag ? t('level3.withNameTag') : null,
  ].filter(Boolean);

  // A cor do desgaste vira o brilho atrás da arma: verde na Nova de Fábrica,
  // vermelho na Bem Desgastada. Decora e informa ao mesmo tempo, e o cliente
  // reconhece a condição antes de ler o rótulo.
  const brilho = listing.exterior?.color ?? 'var(--accent)';

  return (
    <div className="row" style={{ ['--brilho' as string]: brilho }}>
      <div className="rank">{rank}</div>

      <div className="thumb-col">
      {/* A miniatura concentra as duas ações que antes eram botões de texto:
          clicar na foto abre maior, e a lupa abre no CS2. Passar o mouse já
          mostra a prévia, para não precisar abrir nada no caso comum. */}
      <div className="thumb-wrap" ref={miniatura} onMouseEnter={aoEntrar}>
        <span className="thumb-palco" aria-hidden />
        <button
          type="button"
          className="thumb-open"
          disabled={!image}
          title={realImage ? t('level3.zoom') : t('level3.modelImage')}
          onClick={abrirImagem}
        >
          {icon ? <img className="thumb" src={icon} alt="" loading="lazy" /> : <div className="thumb" />}
        </button>
        {espiou && image && (
          <div className={`thumb-preview${paraBaixo ? ' abaixo' : ''}`}>
            <img src={image} alt="" />
            {!realImage && <span>{t('level3.modelImageCaption')}</span>}
          </div>
        )}
      </div>

      {/* Onde a peça cai na escala de desgaste, embaixo da arma.
          O número exato já está na linha ao lado, com as oito casas que se
          copia; aqui o valor é a POSIÇÃO — verde à esquerda, vermelho à
          direita — que se lê num relance ao comparar vinte anúncios. */}
      {temFloat && (
        <div className="wear-bar" title={`Float ${floatValue(listing.float)}`}>
          <i style={{ left: `${Math.min(100, Math.max(0, listing.float! * 100))}%` }} />
        </div>
      )}
      </div>

      <div className="main">
        {/* Uma informação por linha: desgaste → float → pattern. */}
        <div className="title-line">
          {listing.exterior && <span className="title">{loc(listing.exterior)}</span>}
          {estilo && (
            <span
              className="style-pill"
              style={{ color: estilo.color, borderColor: estilo.color }}
              title={estilo.key.startsWith('tier') ? t('level3.tier') : undefined}
            >
              <i style={{ background: estilo.color }} aria-hidden />
              {loc({ pt: estilo.pt, en: estilo.en })}
            </span>
          )}
        </div>
        <div className="sub">
          {t('level3.float')}{' '}
          <span style={{ color: 'var(--accent2)', fontWeight: 700, fontSize: 12 }}>{floatValue(listing.float)}</span>
        </div>
        {details.length > 0 && <div className="sub">{details.join(' · ')}</div>}
        {(listing.stickers.length > 0 || listing.charms.length > 0) && (
          <div className="sticker-strip">
            {listing.stickers.slice(0, 6).map((s, i) => {
              const label = [
                s.nameEn ?? s.name,
                s.wear ? t('level3.stickerWear', { wear: s.wear }) : null,
                s.price ? money(s.price) : null,
              ]
                .filter(Boolean)
                .join(' · ');
              return s.imageUrl ? (
                <img key={`s${i}`} src={s.imageUrl} alt={s.name ?? t('level3.sticker')} title={label} />
              ) : (
                <span key={`s${i}`} className="pill" title={label}>
                  🎯 {s.name ?? t('level3.sticker')}
                </span>
              );
            })}
            {listing.charms.map((c, i) =>
              c.imageUrl ? (
                <img key={`c${i}`} src={c.imageUrl} alt={c.name ?? t('level3.charm')} title={c.name ?? t('level3.charm')} />
              ) : (
                <span key={`c${i}`} className="pill" title={t('level3.charm')}>
                  🔗 {c.name ?? t('level3.charm')}
                </span>
              ),
            )}
          </div>
        )}
      </div>

      <div className="right">
        <div className="price">{money(listing.price)}</div>
        <div className="actions">
          {/* Inspecionar deixou de ser a lupinha na miniatura: era a ação que
              mais se usa antes de comprar e a menos visível da linha. Aqui ela
              tem o peso da decisão, ao lado do preço e do Comprar. */}
          {listing.inspectUrl ? (
            <a className="btn small inspect" href={listing.inspectUrl} title={t('level3.inspectTitle')}>
              <svg viewBox="0 0 24 24" width="13" height="13" aria-hidden>
                <circle cx="11" cy="11" r="6.5" fill="none" stroke="currentColor" strokeWidth="2.2" />
                <line x1="16" y1="16" x2="21" y2="21" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
              </svg>
              Inspecionar no Jogo
            </a>
          ) : (
            <span className="btn small inspect off" title={t('level3.noInspect')}>
              sem inspeção
            </span>
          )}

          {/* A peça é escolhida aqui, com float e pattern à vista — é este
              anúncio que vai para a sacola, não o desgaste em geral. */}
          <button
            className={`btn small comprar ${naSacola ? 'ghost' : 'primary'}`}
            disabled={listing.price === null}
            title={naSacola ? 'Já está na sacola' : 'Adicionar esta peça à sacola'}
            onClick={() => {
              if (naSacola) trackRemoveFromCart(itemDoAnuncio(listing));
              else trackAddToCart(itemDoAnuncio(listing));
              return naSacola
                ? carrinho.remover(listing.commodityNo)
                : carrinho.adicionar(
                    linhaDoAnuncio(listing, {
                      rate,
                      exterior: listing.exterior ? loc(listing.exterior) : null,
                      iconReserva: fallbackIcon,
                    }),
                  );
            }}
          >
            {naSacola ? (
              <>
                <svg viewBox="0 0 24 24" width="13" height="13" aria-hidden>
                  <path
                    d="M5 12.5l4.5 4.5L19 7.5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                na sacola
              </>
            ) : (
              <>
                <svg viewBox="0 0 24 24" width="13" height="13" aria-hidden>
                  <path
                    d="M3.2 4h2.3l2.2 10.2h9.4l1.9-7.2H7"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <circle cx="9.5" cy="19" r="1.5" fill="currentColor" />
                  <circle cx="16.5" cy="19" r="1.5" fill="currentColor" />
                </svg>
                Comprar
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
